import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import VideoModal from "@common/components/VideoModal";
import { Cloudinary } from "@cloudinary/url-gen";
import posterImage from "@common/assets/images/poster.webp";
import videoFile from "@common/assets/videos/brand.mp4";
import userspeak from "@common/assets/images/icons/user-speak.svg";
import banknote from "@common/assets/images/icons/banknote.svg";
import quitpip from "@common/assets/images/icons/quit-pip.svg";

export default function TradePage() {
  const [activeProject, setActiveProject] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [visibleProject, setVisibleProject] = useState(null);

  // Create Cloudinary instance for poster image
  const cld = new Cloudinary({
    cloud: {
      cloudName: "dci1aiukm",
    },
  });

  // Create poster image for video
  const videoPosterImage = cld.image("sample");

  // Project data
  const projects = [
    {
      title: "LUXURY RESIDENCE",
      location: "MUMBAI / RESIDENTIAL / 2024",
      description:
        "Modern window treatments for a premium residential project featuring custom blinds and automated solutions.",
      image:
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop",
    },
    {
      title: "CORPORATE OFFICE",
      location: "DELHI / COMMERCIAL / 2024",
      description:
        "Large-scale commercial installation with energy-efficient blinds and smart control systems.",
      image:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop",
    },
    {
      title: "BOUTIQUE HOTEL",
      location: "BANGALORE / HOSPITALITY / 2024",
      description:
        "Elegant window solutions for a luxury hotel featuring blackout curtains and decorative elements.",
      image:
        "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop",
    },
    {
      title: "RETAIL SHOWROOM",
      location: "CHENNAI / RETAIL / 2024",
      description:
        "Contemporary window treatments for a flagship store with motorized blinds and UV protection.",
      image:
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop",
    },
  ];

  // Auto-cycle through projects every 4 seconds
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setActiveProject((prev) => (prev + 1) % projects.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isHovered, projects.length]);

  // Intersection Observer for mobile auto-expand
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const observerOptions = {
      root: null,
      rootMargin: "-40% 0px -40% 0px", // Trigger when tile is in the middle 20% of viewport
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const projectIndex = parseInt(entry.target.dataset.projectIndex);
        if (entry.isIntersecting) {
          setVisibleProject(projectIndex);
        }
      });
    }, observerOptions);

    // Observe all project tiles
    const projectTiles = document.querySelectorAll(
      ".project-tile:not(.view-more-tile)"
    );
    projectTiles.forEach((tile) => {
      observer.observe(tile);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleProjectHover = (index) => {
    setActiveProject(index);
    setIsHovered(true);
  };

  const handleProjectLeave = () => {
    setIsHovered(false);
  };

  return (
    <div className="trade-page">
      {/* Hero Section with Video */}
      <div className="hero-section">
        <div className="hero-container">
          <div className="hero-image">
            <div className="image-wrapper">
              <VideoModal
                posterImage={videoPosterImage}
                videoUrl={videoFile}
                videoImage={posterImage}
              />
            </div>
          </div>
          <div className="hero-content">
            <div className="hero-text">
              <h1>OWN A SANSAAR EXPERIENCE</h1>
              <p>
                Join the Sansaar family and become part of a brand that blends
                timeless aesthetics with modern functionality. As a franchise
                partner, you'll represent India's leading name in window
                fashions, bringing thoughtfully designed blinds and soft
                furnishings to your local market.
              </p>
              <button className="contact-btn" onClick={() => window.location.href = "/franchise-enquiry"}>CONTACT US</button>
            </div>
          </div>
        </div>
      </div>

      {/* Scale Section */}
      <div className="scale-section">
        <div className="scale-content">
          <h2>
            DESIGNED FOR SCALE.
            <br />
            DELIVERED WITH PRECISION.
          </h2>
          <p>
            Sansaar partners with large architects, interior designers,
            hospitality brands, and corporates to deliver high-quality window
            solutions that meet diverse project needs.
          </p>
        </div>

        {/* Services Section */}
        <div className="services-grid">
          <div className="service-card">
            <div className="service-image">
              <img
                src="https://images.unsplash.com/photo-1760915170483-3e943d42f29a?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=687"
                alt="Dedicated service team"
              />
            </div>
            <div className="service-content">
              <div className="service-icon">
                <img src={userspeak} alt="User speak icon" />
              </div>
              <h3>Dedicated service team</h3>
              <p>
                A professional crew that manages every stage from design
                consultation to final installation.
              </p>
            </div>
          </div>

          <div className="service-card">
            <div className="service-image">
              <img
                src="https://images.unsplash.com/photo-1760915170483-3e943d42f29a?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=687"
                alt="Bulk pricing"
              />
            </div>
            <div className="service-content">
              <div className="service-icon">
                <img src={banknote} alt="Banknote icon" />
              </div>
              <h3>Bulk pricing</h3>
              <p>
                Competitive rates tailored for large-scale orders without
                compromising on quality.
              </p>
            </div>
          </div>

          <div className="service-card">
            <div className="service-image">
              <img
                src="https://images.unsplash.com/photo-1760915170483-3e943d42f29a?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=687"
                alt="Customisation"
              />
            </div>
            <div className="service-content">
              <div className="service-icon">
                <img src={quitpip} alt="Customisation icon" />
              </div>
              <h3>Customisation</h3>
              <p>
                Solutions designed to align with your project's aesthetic and
                functional goals.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Showcase Section */}
      <div className="project-showcase-section">
        <motion.div
          className="showcase-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2>OUR PROJECT SHOWCASE</h2>
          <p>
            Explore some of our recent collaborations with leading architects
            and interior brands. Each project reflects Sansaar's commitment to
            craftsmanship, detail, and seamless execution.
          </p>
        </motion.div>

        {/* Project Tiles Row */}
        <div className="project-tiles-container">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className={`project-tile ${
                activeProject === index ? "active" : ""
              } ${visibleProject === index ? "mobile-expanded" : ""}`}
              data-project-index={index}
              onMouseEnter={() => handleProjectHover(index)}
              onMouseLeave={handleProjectLeave}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="project-description-overlay">
                <h3>{project.title}</h3>
                <p className="project-location">{project.location}</p>
                <p className="project-description">{project.description}</p>
              </div>
              <div className="project-content">
                <div className="project-image">
                  <img src={project.image} alt={project.title} loading="lazy" />
                </div>
              </div>
              <div className="mobile-project-details">
                <h3>{project.title}</h3>
                <p className="project-location">{project.location}</p>
                <p className="project-description">{project.description}</p>
              </div>
            </motion.div>
          ))}

          {/* View More Tile */}
          <motion.div
            className="project-tile view-more-tile"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: projects.length * 0.1 }}
            viewport={{ once: true }}
          >
            <div className="view-more-content">
              <div className="count">12+</div>
              <div className="view-more-text">VIEW MORE</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Network Section */}
      <div className="network-section">
        <motion.div
          className="network-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2>JOIN INDIA'S TRUSTED NETWORK</h2>
          <p className="network-subtitle">
            A growing network of <strong>400+ stores</strong> across India
          </p>
        </motion.div>

        {/* Store Images Grid */}
        <motion.div
          className="network-stores-grid"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="store-image">
            <img
              src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=500&fit=crop&crop=center"
              alt="Modern Sansaar Showroom Interior"
              loading="lazy"
            />
          </div>
          <div className="store-image">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=500&fit=crop&crop=center"
              alt="Sansaar Store Display Area"
              loading="lazy"
            />
          </div>
          <div className="store-image">
            <img
              src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=500&fit=crop&crop=center"
              alt="Sansaar Product Showcase Wall"
              loading="lazy"
            />
          </div>
        </motion.div>

        {/* Network Description */}
        <motion.div
          className="network-description"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p>
            Sansaar invites you to become part of its expanding distribution
            family. Our robust product portfolio, strong brand presence, and
            supply-chain support empower you to build a successful business with
            confidence.
          </p>
          <button onClick={() => window.location.href = "/franchise-enquiry"} className="contact-btn network-contact-btn">
            CONTACT US
          </button>
        </motion.div>
      </div>
    </div>
  );
}
